import { useEffect, useState } from "react";
import axios from "axios";
import {
  FaCheckCircle,
  FaHourglassHalf,
  FaTimesCircle,
  FaFolderOpen,
  FaMusic,
  FaMoneyBill,
} from "react-icons/fa";
import { FaChevronCircleLeft } from "react-icons/fa";
import { FaCircleChevronRight } from "react-icons/fa6";
import { useToast } from "@chakra-ui/react";
import { FiUser } from "react-icons/fi";
import UserDetailsModal from "../components/UserDetailsModal";

const BankApproval = () => {
  const toast = useToast();
 const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userModalModel, setUserModalModel] = useState(null);
  const [userModalUserId, setUserModalUserId] = useState(null);

  const limit = 10;

  const openUserProfile = async (u) => {
    if (!u) return;
    const id = u._id || u.id;
    if (!id) return;
    const baseModel = {
      name: u.fullName || u.name || u.email || u.phoneNumber || id,
      image: u.profileImage || u.image || u.avatar || "https://via.placeholder.com/150",
      gender: u.gender || "-",
      email: u.email,
      phone: u.phoneNumber,
    };
    setUserModalModel(baseModel);
    setUserModalUserId(id);
    setUserModalOpen(true);

    try {
      const res = await axios.get(`/api/v1/app/profile/${id}`);
      const p = res?.data?.data || {};
      const getAge = (dobStr) => {
        if (!dobStr) return undefined;
        const dob = new Date(dobStr);
        const diff = Date.now() - dob.getTime();
        const ageDt = new Date(diff);
        return Math.abs(ageDt.getUTCFullYear() - 1970);
      };
      const enriched = {
        name: p.fullName || p.name || baseModel.name,
        email: p.email || p.contactEmail || baseModel.email,
        phone: p.phoneNumber || p.phone || baseModel.phone,
        address: p.city || p.address || p.location?.city,
        latitude: p.coordinates?.latitude ?? p.location?.latitude,
        longitude: p.coordinates?.longitude ?? p.location?.longitude,
        age: getAge(p.dateOfBirth),
        dob: p.dateOfBirth ? new Date(p.dateOfBirth).toLocaleDateString() : undefined,
        gender: p.gender || baseModel.gender,
        role: p.role,
        lastActive: p.lastActive ? new Date(p.lastActive).toLocaleString() : undefined,
        accountCreated: p.createdAt ? new Date(p.createdAt).toLocaleString() : undefined,
        followers: Array.isArray(p.followers) ? p.followers.length : undefined,
        following: Array.isArray(p.following) ? p.following.length : undefined,
        friends: Array.isArray(p.friends) ? p.friends.length : undefined,
        image: p.profileImage || p.image || baseModel.image,
      };
      setUserModalModel((prev) => ({ ...prev, ...enriched }));
    } catch (e) {
      // ignore enrichment error
    }
  };
  // ===== Fetch Users =====
  const fetchUsers = async (status = "all", pageNum = 1) => {
    try {
      setLoading(true);

      const res = await axios.get("/api/v2/bank/admin/all", {
        params: { status, page: pageNum, limit, search },
      });

      setUsers(res.data.data);
      setTotalPages(res.data.totalPages);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ===== Fetch Stats =====
  const fetchStats = async () => {
    try {
      const res = await axios.get("/api/v2/bank/admin/stats");
      setStats(res.data.stats);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers(selectedFilter, page);
    fetchStats();
  }, [page, selectedFilter,search ]);

  // ===== Approve/Reject =====
  const updateStatus = async (id, status) => {
    try {
      await axios.put(`/api/v2/bank/admin/${id}`, { status });

      toast({
        title: `Request ${status}`,
        status: "success",
        duration: 2000,
      });

      fetchUsers(selectedFilter, page);
      fetchStats();

    } catch (err) {
      toast({
        title: "Error updating status",
        status: "error",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64 bg-[var(--black)]">
        <div className="w-8 h-8 border-4 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-[var(--black)] text-[var(--yellow)]">

      {/* ===== Header ===== */}
      <div className="flex justify-between mb-6">
        <h1 className="text-3xl font-bold">Bank Verification</h1>

        <select
          value={selectedFilter}
          onChange={(e) => {
            setSelectedFilter(e.target.value);
            setPage(1);
          }}
          className="bg-[var(--black)] border px-4 py-2 rounded-xl"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* ===== Stats ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total" value={stats.totalRequests} icon={<FaFolderOpen />} />
        <StatCard title="Pending" value={stats.totalPending} icon={<FaHourglassHalf />} />
        <StatCard title="Approved" value={stats.totalApproved} icon={<FaCheckCircle />} />
        <StatCard title="Rejected" value={stats.totalRejected} icon={<FaTimesCircle />} />
      </div>


 <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FaMoneyBill className="text-[var(--yellow)] text-xl" />
            <h2 className="text-xl font-bold text-[var(--yellow)]">Users For Bank Verifcation</h2>
          </div>
  
        </div>
        <p className="text-[color:var(--yellow)] mb-4">Review, approve or reject bank Verification submissions</p>

    {/* ===== List ===== */}
<div className="space-y-4">
  {users.length === 0 ? (
    <div className="border rounded-xl p-8 text-center bg-[var(--black)]">
      <FiUser className="mx-auto text-5xl text-gray-500 mb-3" />
      
      <h2 className="text-xl font-bold text-[var(--yellow)]">
        No Users Found
      </h2>

      <p className="text-gray-400 mt-2">
        No bank verification requests are available.
      </p>
    </div>
  ) : (
    users.map((u) => (
      <div
        key={u._id}
        className="border rounded-xl p-4 flex justify-between items-center"
      >
        <div className="flex items-center gap-4">
          
          {/* Profile Image */}
          <div className="w-12 h-12 rounded-full overflow-hidden border bg-gray-200 flex items-center justify-center">
            {u.userId?.profileImage ? (
              <img
                src={u.userId.profileImage}
                alt="profile"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/default-avatar.png";
                }}
              />
            ) : (
              <FiUser className="text-gray-500 text-xl" />
            )}
          </div>

          {/* User Info */}
          <div>
            <p className="font-bold">
              Account HolderName: {u.name}
            </p>
            <p>UserName: {u?.userId?.fullName}</p>
            <p>PAN: {u.panNumber}</p>
            <p>UPI: {u.upiId}</p>

            <span className="text-sm bg-purple-700 text-black px-2 py-1 rounded">
              {u.status.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          {u.status !== "approved" && (
            <button
              onClick={() => updateStatus(u._id, "approved")}
              className="bg-green-500 px-4 py-2 rounded text-white"
            >
              Approve
            </button>
          )}

          {u.status !== "rejected" && (
            <button
              onClick={() => updateStatus(u._id, "rejected")}
              className="bg-red-500 px-4 py-2 rounded text-white"
            >
              Reject
            </button>
          )}
        </div>
      </div>
    ))
  )}
</div>

      {/* ===== Pagination ===== */}
      <div className="flex justify-center mt-8 gap-3">
        <button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          className="[var(--black)] text-black px-3 py-2 rounded">
          <FaChevronCircleLeft />
        </button>

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => setPage(i + 1)}
            className={`w-8 h-8 rounded ${
              page === i + 1
                ? "[var(--black)] text-black"
                : "border"
            }`}>
            {i + 1}
          </button>
        ))}

        <button
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
          className="[var(--black)] text-black px-3 py-2 rounded">
          <FaCircleChevronRight />
        </button>
      </div>
      {userModalOpen && userModalModel && userModalUserId && (
        <UserDetailsModal
          model={userModalModel}
          userId={userModalUserId}
          onClose={() => setUserModalOpen(false)}
        />
      )}
    </div>
  );
};

// ===== Stat Card =====
const StatCard = ({ title, value, icon }) => (
  <div className="border rounded-xl p-4 flex justify-between items-center">
    <div>
      <p className="text-sm">{title}</p>
      <h2 className="text-2xl font-bold">{value || 0}</h2>
    </div>
    <div className="text-2xl">{icon}</div>
  </div>
);

export default BankApproval;
